import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { ArrowRight, ExternalLink, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseStudiesPage() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedStudy, setSelectedStudy] = useState(null);

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {
    try {
      const response = await axios.get(`${API}/case-studies`);
      setCaseStudies(response.data);
    } catch (error) {
      console.error('Failed to fetch case studies:', error);
    }
  };

  const categories = ['All', ...new Set(caseStudies.map(s => s.category))];
  
  const filteredStudies = selectedCategory === 'All' 
    ? caseStudies 
    : caseStudies.filter(s => s.category === selectedCategory);

  return (
    <div data-testid="case-studies-page" className="min-h-screen pt-16">
      {/* Hero */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#05050A]">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-[150px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <Badge className="mb-6 bg-primary/10 text-primary border-primary/30 font-mono text-xs">
              SUCCESS STORIES
            </Badge>
            <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight mb-6">
              Case Studies
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Explore how we&apos;ve helped organizations across industries transform 
              their technology infrastructure and achieve measurable results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-y border-white/10 bg-black/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 text-sm uppercase tracking-wider transition-all ${
                  selectedCategory === category
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-white/5 text-muted-foreground hover:bg-white/10'
                }`}
                data-testid={`filter-${category.toLowerCase().replace(' ', '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {filteredStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-card/30 border border-white/10 overflow-hidden hover:border-primary/30 transition-all duration-500 cursor-pointer"
                onClick={() => setSelectedStudy(study)}
                data-testid={`case-study-${study.id}`}
              >
                <div className="aspect-video relative overflow-hidden">
                  <img 
                    src={study.image_url} 
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  
                  {/* Overlay content */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <Badge className="mb-3 bg-primary/20 text-primary border-primary/30 text-xs">
                      {study.category}
                    </Badge>
                    <h3 className="font-heading text-2xl font-semibold group-hover:text-primary transition-colors">
                      {study.title}
                    </h3>
                  </div>
                </div>
                
                <div className="p-6">
                  <p className="text-muted-foreground mb-4 line-clamp-2">
                    {study.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <span className="text-muted-foreground">Client: </span>
                      <span className="text-foreground">{study.client_name}</span>
                    </div>
                    <Badge variant="outline" className="border-white/20">
                      {study.industry}
                    </Badge>
                  </div>

                  {/* Results Preview */}
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="flex items-center gap-2 text-primary text-sm">
                      <TrendingUp className="w-4 h-4" />
                      <span className="font-mono">{study.results[0]}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {filteredStudies.length === 0 && (
            <div className="text-center py-16">
              <p className="text-muted-foreground">No case studies found for this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Case Study Modal */}
      {selectedStudy && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setSelectedStudy(null)}
          data-testid="case-study-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-video relative">
              <img 
                src={selectedStudy.image_url} 
                alt={selectedStudy.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
            </div>
            
            <div className="p-8">
              <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">
                {selectedStudy.category}
              </Badge>
              
              <h2 className="font-heading text-3xl font-bold mb-4">
                {selectedStudy.title}
              </h2>
              
              <p className="text-muted-foreground mb-6">
                {selectedStudy.description}
              </p>

              <div className="space-y-6">
                <div>
                  <h4 className="font-heading font-semibold text-lg mb-2 text-primary">Challenge</h4>
                  <p className="text-muted-foreground">{selectedStudy.challenge}</p>
                </div>
                
                <div>
                  <h4 className="font-heading font-semibold text-lg mb-2 text-primary">Solution</h4>
                  <p className="text-muted-foreground">{selectedStudy.solution}</p>
                </div>
                
                <div>
                  <h4 className="font-heading font-semibold text-lg mb-3 text-primary">Results</h4>
                  <ul className="space-y-2">
                    {selectedStudy.results.map((result, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <TrendingUp className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  <span>Client: </span>
                  <span className="text-foreground font-medium">{selectedStudy.client_name}</span>
                  <span className="mx-2">/</span>
                  <span>{selectedStudy.industry}</span>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedStudy(null)}
                  data-testid="close-modal-btn"
                >
                  Close
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* CTA */}
      <section className="py-24 bg-black/30">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Ready to Be Our Next
            <span className="text-primary"> Success Story?</span>
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Join industry leaders who have transformed their operations with SentinelX.
          </p>
          <Link to="/contact">
            <Button 
              size="lg"
              className="bg-primary text-primary-foreground font-bold uppercase tracking-wider btn-chamfer glow-cyan h-14 px-8"
              data-testid="case-studies-cta-btn"
            >
              Start Your Journey
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
